import { type ComponentOverrides } from "theme/theme.types";

/**
 * MUI draws its caret as a rotated square sized in `em`, so both the box and its `::before` are
 * restated here — a `clip-path` triangle replaces the 45deg rotation.
 */
export const TOOLTIP_CARET_BASE = 10;

export const TOOLTIP_CARET_DEPTH = 4;

/**
 * How far the caret box reaches back under the surface. Butted edge to edge, the two antialias
 * separately wherever Popper lands on a fractional pixel, and a hairline of the page shows between
 * them. The overlap is a full-width strip of the triangle's base, so the visible caret is unchanged.
 */
export const TOOLTIP_CARET_OVERLAP = 1;

const CARET_BOX_DEPTH = TOOLTIP_CARET_DEPTH + TOOLTIP_CARET_OVERLAP;

const OVERLAP = `${TOOLTIP_CARET_OVERLAP}px`;

/** The caret per popper placement: the overlap strip on the surface side, then the triangle pointing away. */
const TOOLTIP_CARET_CLIP = {
  top: `polygon(0 0, 100% 0, 100% ${OVERLAP}, 50% 100%, 0 ${OVERLAP})`,
  bottom: `polygon(50% 0, 100% calc(100% - ${OVERLAP}), 100% 100%, 0 100%, 0 calc(100% - ${OVERLAP}))`,
  left: `polygon(0 0, ${OVERLAP} 0, 100% 50%, ${OVERLAP} 100%, 0 100%)`,
  right: `polygon(calc(100% - ${OVERLAP}) 0, 100% 0, 100% 100%, calc(100% - ${OVERLAP}) 100%, 0 50%)`,
};

/**
 * The trigger gap comes from the popper `offset` `ui/tooltip` sets, so MUI's per-placement margins
 * are zeroed or the two stack — through
 * MUI's own `.MuiTooltip-popper[...] &` selector, which a plain `margin` loses to.
 */
export const MuiTooltip: ComponentOverrides["MuiTooltip"] = {
  defaultProps: {
    // MUI waits 100ms.
    enterDelay: 0,
  },
  styleOverrides: {
    tooltip: ({ theme }) => ({
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
      borderRadius: theme.shape.medium,
      padding: theme.spacing(0.5, 1),
      ...theme.typography.body2,
      '.MuiTooltip-popper[data-popper-placement*="top"] &': { marginBottom: 0 },
      '.MuiTooltip-popper[data-popper-placement*="bottom"] &': { marginTop: 0 },
      '.MuiTooltip-popper[data-popper-placement*="left"] &': { marginRight: 0 },
      '.MuiTooltip-popper[data-popper-placement*="right"] &': { marginLeft: 0 },
    }),
    arrow: ({ theme }) => ({
      color: theme.palette.common.black,
      width: TOOLTIP_CARET_BASE,
      height: CARET_BOX_DEPTH,
      "&::before": { transform: "none" },
    }),
    // Per placement here rather than on `arrow`: MUI's own placement rules out-rank the bare
    // caret class and would win on the axis swap.
    popperArrow: {
      '&[data-popper-placement*="top"] .MuiTooltip-arrow': {
        marginBottom: -TOOLTIP_CARET_DEPTH,
        "&::before": { clipPath: TOOLTIP_CARET_CLIP.top },
      },
      '&[data-popper-placement*="bottom"] .MuiTooltip-arrow': {
        marginTop: -TOOLTIP_CARET_DEPTH,
        "&::before": { clipPath: TOOLTIP_CARET_CLIP.bottom },
      },
      '&[data-popper-placement*="left"] .MuiTooltip-arrow': {
        width: CARET_BOX_DEPTH,
        height: TOOLTIP_CARET_BASE,
        marginRight: -TOOLTIP_CARET_DEPTH,
        "&::before": { clipPath: TOOLTIP_CARET_CLIP.left },
      },
      '&[data-popper-placement*="right"] .MuiTooltip-arrow': {
        width: CARET_BOX_DEPTH,
        height: TOOLTIP_CARET_BASE,
        marginLeft: -TOOLTIP_CARET_DEPTH,
        "&::before": { clipPath: TOOLTIP_CARET_CLIP.right },
      },
    },
  },
};
